import { useTranslation } from 'react-i18next';

import {
  type AnyPalette,
  type UserPalette,
  useReorderPalettes,
  useSetFavoritePalette,
} from '@/entities/palette';
import { Icons, MenuItem, MenuList, MenuRow, Popover } from '@/shared/ui';

import styles from './ColorPalettes.module.css';
import { usePaletteNames } from './names';
import { PaletteStrip } from './PaletteStrip';

export interface PaletteActions {
  onEdit: (palette: UserPalette) => void;
  onDuplicate: (palette: AnyPalette) => void;
  onDelete: (palette: UserPalette) => void;
}

/**
 * One palette in a list: name, scope (and workspace for a shared one), its colors, a favorite
 * toggle and the actions the viewer may use. `onPick` makes the colors choosable (the library);
 * without it they are a preview (the workspace page).
 */
export function PaletteRow({
  palette,
  favorite,
  actions,
  onPick,
  personalIds,
}: {
  palette: AnyPalette;
  favorite: boolean;
  actions: PaletteActions;
  onPick?: (color: string) => void;
  /** The ids of the viewer's personal palettes in their order (for moving one up / down). */
  personalIds?: readonly string[];
}) {
  const { t } = useTranslation();
  const { nameOf, descriptionOf } = usePaletteNames();
  const setFavorite = useSetFavoritePalette();
  const reorder = useReorderPalettes();
  const name = nameOf(palette);
  const user = palette.scope === 'system' ? null : palette;
  const index = personalIds?.indexOf(palette.id) ?? -1;
  const move = (delta: -1 | 1) => {
    if (!personalIds) return;
    const next = [...personalIds];
    const [item] = next.splice(index, 1);
    if (item !== undefined) next.splice(index + delta, 0, item);
    reorder.mutate(next);
  };
  const scope =
    user?.scope === 'workspace' && user.workspace_name
      ? `${t('palettes.scope.workspace')} · ${user.workspace_name}`
      : t(`palettes.scope.${palette.scope}`);
  return (
    <li className={styles.row} data-testid="palette-row" data-scope={palette.scope}>
      <div className={styles.rowHead}>
        <div className={styles.rowTitle}>
          <span className={styles.rowName} title={name}>
            {name}
          </span>
          <span className={styles.rowMeta}>
            {scope}
            {user?.creator_name ? ` · ${t('palettes.by', { name: user.creator_name })}` : ''}
            {' · '}
            {t('palettes.colorsCount', { count: palette.colors.length })}
          </span>
          {descriptionOf(palette) && (
            <span className={styles.rowMeta}>{descriptionOf(palette)}</span>
          )}
        </div>
        <button
          type="button"
          className={styles.iconButton}
          aria-pressed={favorite}
          aria-label={t(favorite ? 'palettes.unfavoritePalette' : 'palettes.favoritePalette', {
            name,
          })}
          title={t(favorite ? 'palettes.unfavoritePalette' : 'palettes.favoritePalette', { name })}
          onClick={() => {
            setFavorite.mutate({ ref: palette.id, favorite: !favorite });
          }}
        >
          <span className={favorite ? styles.starOn : undefined}>
            <Icons.star />
          </span>
        </button>
        <Popover
          label={t('palettes.actions', { name })}
          width={240}
          alignEnd
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className={styles.iconButton}
              aria-label={t('palettes.actions', { name })}
            >
              <Icons.more />
            </button>
          )}
        >
          {(close) => (
            <MenuList>
              {user?.can_edit && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.edit />}
                    onClick={() => {
                      close();
                      actions.onEdit(user);
                    }}
                  >
                    {t('palettes.edit')}
                  </MenuItem>
                </MenuRow>
              )}
              <MenuRow>
                <MenuItem
                  icon={<Icons.copy />}
                  onClick={() => {
                    close();
                    actions.onDuplicate(palette);
                  }}
                >
                  {t('palettes.duplicate')}
                </MenuItem>
              </MenuRow>
              {user?.scope === 'personal' && index > 0 && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.arrowUp />}
                    onClick={() => {
                      close();
                      move(-1);
                    }}
                  >
                    {t('palettes.moveUp')}
                  </MenuItem>
                </MenuRow>
              )}
              {user?.scope === 'personal' &&
                personalIds &&
                index >= 0 &&
                index < personalIds.length - 1 && (
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.arrowDown />}
                      onClick={() => {
                        close();
                        move(1);
                      }}
                    >
                      {t('palettes.moveDown')}
                    </MenuItem>
                  </MenuRow>
                )}
              {user?.can_delete && (
                <MenuRow separated>
                  <MenuItem
                    icon={<Icons.trash />}
                    danger
                    onClick={() => {
                      close();
                      actions.onDelete(user);
                    }}
                  >
                    {t('palettes.delete')}
                  </MenuItem>
                </MenuRow>
              )}
            </MenuList>
          )}
        </Popover>
      </div>
      <PaletteStrip colors={palette.colors} label={name} {...(onPick && { onPick })} />
    </li>
  );
}
